import { useEffect, useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { 
  getReviewRequests, 
  getReviewRequestStats, 
  getReviewRequest,
  actionReviewRequest,
  getCall,
  logCallAudit
} from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  ClipboardCheck, Clock, CheckCircle, XCircle, AlertCircle,
  MessageSquare, User, Phone, Calendar, Star, FileText,
  ChevronLeft, ChevronRight, RefreshCw, Filter, Search,
  Eye, Handshake, ArrowRight, Loader2, Play, TrendingUp
} from 'lucide-react';
import { format } from 'date-fns';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: Clock },
  in_review: { label: 'In Review', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: Eye },
  approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', icon: CheckCircle },
  declined: { label: 'Declined', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: XCircle },
  completed: { label: 'Completed', color: 'bg-muted text-slate-700 dark:bg-slate-800 dark:text-muted-foreground', icon: CheckCircle },
};

export default function ReviewRequests() {
  const outletContext = useOutletContext() || {};
  const adminUrl = outletContext.adminUrl;
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('actionable');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Action dialog state
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [actionType, setActionType] = useState('approve');
  const [reviewNotes, setReviewNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Call detail preview
  const [previewCall, setPreviewCall] = useState(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);

  const fetchStats = async () => {
    try {
      const { data } = await getReviewRequestStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchRequests = async (status = null) => {
    setIsLoading(true);
    try {
      const params = { page, limit: 20 };
      if (status === 'actionable') {
        params.status = 'actionable';
      } else if (status && status !== 'all') {
        params.status = status;
      }
      
      const { data } = await getReviewRequests(params);
      setRequests(data.requests || []);
      setTotalPages(data.total_pages || 1);
    } catch (error) {
      toast.error('Failed to fetch review requests');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchRequests(activeTab);
  }, [activeTab, page]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
  };

  const openActionDialog = (request, action) => {
    setSelectedRequest(request);
    setActionType(action);
    setReviewNotes('');
    setShowActionDialog(true);
  };

  const handleAction = async () => {
    if (!selectedRequest) return;
    
    if (!reviewNotes.trim() && (actionType === 'decline' || actionType === 'approve')) {
      toast.error('Please provide review notes');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await actionReviewRequest(
        selectedRequest.id,
        { action: actionType, review_notes: reviewNotes },
        user?.id || 'admin',
        user?.name || 'Admin',
        user?.role || 'admin'
      );
      
      // Log audit event for the call
      const auditAction = actionType === 'approve' ? 'review_approved' : 'review_declined';
      await logCallAudit(
        { 
          call_id: selectedRequest.call_id, 
          action: auditAction, 
          details: { 
            request_id: selectedRequest.id,
            requester: selectedRequest.requester_name,
            review_notes: reviewNotes.substring(0, 100)
          }
        },
        user?.id || 'admin',
        user?.name || 'Admin',
        user?.role || 'admin',
        'admin'
      ).catch(console.log);
      
      toast.success(`Request ${actionType}d successfully`);
      setShowActionDialog(false);
      fetchStats();
      fetchRequests(activeTab);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${actionType} request`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openCallPreview = async (callId) => {
    try {
      const { data } = await getCall(callId);
      setPreviewCall(data);
      setShowPreviewDialog(true);
    } catch (error) {
      toast.error('Failed to load call details');
    }
  };

  const navigateToCall = (callId) => {
    const basePath = adminUrl ? `/admin/${adminUrl}` : '/dashboard';
    navigate(`${basePath}/call/${callId}`);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'MMM d, yyyy h:mm a');
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6" data-testid="review-requests-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Call Review Requests</h1>
          <p className="text-sm text-muted-foreground">
            Manage review requests from users seeking QA feedback on their calls
          </p>
        </div>
        <Button variant="outline" onClick={() => { fetchStats(); fetchRequests(activeTab); }}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="card-elevated">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.actionable || 0}</p>
                <p className="text-xs text-muted-foreground">Action Needed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-elevated">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <Eye className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.in_review || 0}</p>
                <p className="text-xs text-muted-foreground">In Review</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-elevated">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.approved || 0}</p>
                <p className="text-xs text-muted-foreground">Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-elevated">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.declined || 0}</p>
                <p className="text-xs text-muted-foreground">Declined</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-elevated">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                <TrendingUp className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.this_week_received || 0}</p>
                <p className="text-xs text-muted-foreground">This Week</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-elevated">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-muted dark:bg-slate-800">
                <ClipboardCheck className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total || 0}</p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="actionable" className="gap-2">
            <AlertCircle className="h-4 w-4" />
            Action Needed
            {stats?.actionable > 0 && (
              <Badge variant="secondary" className="ml-1 bg-amber-100 text-amber-700">
                {stats.actionable}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="approved" className="gap-2">
            <CheckCircle className="h-4 w-4" />
            Approved
          </TabsTrigger>
          <TabsTrigger value="declined" className="gap-2">
            <XCircle className="h-4 w-4" />
            Declined
          </TabsTrigger>
          <TabsTrigger value="all" className="gap-2">
            <ClipboardCheck className="h-4 w-4" />
            All Requests
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          ) : requests.length === 0 ? (
            <Card className="card-elevated">
              <CardContent className="py-12 text-center">
                <ClipboardCheck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium text-foreground">No review requests</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {activeTab === 'actionable' 
                    ? 'All requests have been handled!' 
                    : 'No requests match this filter'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {requests.map((request) => {
                const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.pending;
                const StatusIcon = statusConfig.icon;
                
                return (
                  <Card key={request.id} className="card-elevated hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigateToCall(request.call_id)}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        {/* Status Icon */}
                        <div className={`p-3 rounded-lg ${statusConfig.color}`}>
                          <StatusIcon className="h-5 w-5" />
                        </div>
                        
                        {/* Main Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1 cursor-pointer" onClick={() => navigateToCall(request.call_id)}>
                                <h3 className="font-medium text-foreground truncate hover:text-primary hover:underline">
                                  {request.call_filename || request.call_id}
                                </h3>
                                <Badge className={statusConfig.color}>
                                  {statusConfig.label}
                                </Badge>
                              </div>
                              
                              <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                                <span className="flex items-center gap-1">
                                  <User className="h-3.5 w-3.5" />
                                  {request.requester_name}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-3.5 w-3.5" />
                                  {formatDate(request.created_at)}
                                </span>
                                {request.current_score && (
                                  <span className="flex items-center gap-1">
                                    <Star className="h-3.5 w-3.5" />
                                    Score: {request.current_score.toFixed(1)}
                                  </span>
                                )}
                              </div>
                              
                              <p className="text-sm text-muted-foreground line-clamp-2">
                                <span className="font-medium">Reason:</span> {request.reason}
                              </p>
                              
                              {request.expected_outcome && (
                                <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                                  <span className="font-medium">Expected:</span> {request.expected_outcome}
                                </p>
                              )}
                            </div>
                            
                            {/* Actions */}
                            <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); openCallPreview(request.call_id); }}
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                Preview
                              </Button>
                              
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); navigateToCall(request.call_id); }}
                              >
                                <Play className="h-4 w-4 mr-1" />
                                View Call
                              </Button>
                              
                              {(request.status === 'pending' || request.status === 'in_review') && (
                                <>
                                  <Button 
                                    size="sm"
                                    className="bg-emerald-600 hover:bg-emerald-700"
                                    onClick={(e) => { e.stopPropagation(); openActionDialog(request, 'approve'); }}
                                  >
                                    <CheckCircle className="h-4 w-4 mr-1" />
                                    Approve
                                  </Button>
                                  <Button 
                                    variant="destructive"
                                    size="sm"
                                    onClick={(e) => { e.stopPropagation(); openActionDialog(request, 'decline'); }}
                                  >
                                    <XCircle className="h-4 w-4 mr-1" />
                                    Decline
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                          
                          {/* Review Notes (if completed) */}
                          {request.review_notes && (
                            <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                              <p className="text-sm">
                                <span className="font-medium">Review Notes:</span> {request.review_notes}
                              </p>
                              {request.reviewer_name && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Reviewed by {request.reviewer_name} on {formatDate(request.completed_at)}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Action Dialog */}
      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {actionType === 'approve' ? 'Approve Review Request' : 
               actionType === 'decline' ? 'Decline Review Request' : 
               'Review Request'}
            </DialogTitle>
            <DialogDescription>
              {actionType === 'approve' 
                ? 'Approve this request and optionally collaborate on the call to update scores.'
                : 'Provide feedback explaining why this request is being declined.'}
            </DialogDescription>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm font-medium">{selectedRequest.call_filename}</p>
                <p className="text-xs text-muted-foreground">
                  Requested by {selectedRequest.requester_name}
                </p>
              </div>
              
              <div>
                <Label>Review Notes *</Label>
                <Textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder={actionType === 'approve' 
                    ? 'Explain your decision and any feedback for the user...'
                    : 'Explain why this request is being declined...'}
                  className="mt-1"
                  rows={4}
                />
              </div>
              
              {actionType === 'approve' && (
                <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
                  <p className="text-sm text-emerald-700 dark:text-emerald-400">
                    <strong>Tip:</strong> After approving, you can go to the call detail page and use 
                    "Let's Collaborate" to update the scores with your expert review.
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActionDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAction}
              disabled={isSubmitting}
              className={actionType === 'decline' ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  {actionType === 'approve' ? <CheckCircle className="h-4 w-4 mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
                  {actionType === 'approve' ? 'Approve Request' : 'Decline Request'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Call Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Call Preview</DialogTitle>
          </DialogHeader>
          
          {previewCall && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Filename</Label>
                  <p className="font-medium">{previewCall.filename}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Score</Label>
                  <p className="font-medium text-lg">
                    {previewCall.average_score?.toFixed(1) || 'N/A'}/10
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Classification</Label>
                  <Badge variant="outline" className="capitalize">
                    {previewCall.classification || 'Unclassified'}
                  </Badge>
                </div>
                <div>
                  <Label className="text-muted-foreground">Duration</Label>
                  <p className="font-medium">
                    {previewCall.duration ? `${Math.floor(previewCall.duration / 60)}:${(previewCall.duration % 60).toString().padStart(2, '0')}` : '-'}
                  </p>
                </div>
              </div>
              
              {previewCall.ai_feedback && (
                <div>
                  <Label className="text-muted-foreground">AI Feedback</Label>
                  <p className="text-sm mt-1 line-clamp-4">{previewCall.ai_feedback}</p>
                </div>
              )}
              
              <Button 
                className="w-full"
                onClick={() => {
                  setShowPreviewDialog(false);
                  navigateToCall(previewCall.id);
                }}
              >
                <ArrowRight className="h-4 w-4 mr-2" />
                Go to Full Call Detail
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
